import { buildBridgePreamble } from '@/components/artifact/bridge';
import { stripFence } from '@/components/artifact/utils';
import { DashboardProfilePanel } from '@/components/dashboard/DashboardProfilePanel';
import { DashboardSettingsDialog } from '@/components/dashboard/DashboardSettingsDialog';
import { DashboardShareDialog } from '@/components/dashboard/DashboardShareDialog';
import { useDashboardBridge } from '@/components/dashboard/useDashboardBridge';
import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Check,
  Loader2,
  Settings,
  Share2,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

interface Props {
  dashboardId: string;
}

const DEFAULT_WRAPPER_HEAD =
  '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;font-family:system-ui,-apple-system,"Segoe UI",sans-serif;background:#fff}canvas{max-height:480px!important}[class*="chart"]{max-height:600px;overflow:hidden}</style></head><body>';
const DEFAULT_WRAPPER_TAIL = '</body></html>';

/**
 * /dashboard/view/:id — 영속 dashboard 의 풀스크린 viewer.
 * iframe 안에서 inline JS 가 `window.artifact.runSavedQuery` 호출 → useDashboardBridge 가
 * `/api/dashboards/:id/rpc` 로 프록시. viewer 권한으로 saved_query 재실행.
 * owner 면 헤더에 ⚙ 설정 / 공유 / 삭제 버튼.
 */
export function DashboardViewer({ dashboardId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isExpired, setIsExpired] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['dashboards', 'detail', dashboardId],
    queryFn: () => api.dashboardGet(dashboardId),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const handleQueryExpired = useCallback(() => setIsExpired(true), []);
  useDashboardBridge(iframeRef, dashboardId, { onQueryExpired: handleQueryExpired });

  const detail = detailQuery.data;

  const srcDoc = useMemo(() => {
    if (!detail) return '';
    const stripped = stripFence(detail.renderedHtml, 'html').trim();
    const head = stripped.slice(0, 64).toLowerCase();
    const bridge = buildBridgePreamble();
    if (head.startsWith('<!doctype') || head.startsWith('<html')) {
      const headMatch = stripped.match(/<head[^>]*>/i);
      if (headMatch && headMatch.index !== undefined) {
        const insertAt = headMatch.index + headMatch[0].length;
        return stripped.slice(0, insertAt) + bridge + stripped.slice(insertAt);
      }
      const htmlMatch = stripped.match(/<html[^>]*>/i);
      if (htmlMatch && htmlMatch.index !== undefined) {
        const insertAt = htmlMatch.index + htmlMatch[0].length;
        return stripped.slice(0, insertAt) + bridge + stripped.slice(insertAt);
      }
      return bridge + stripped;
    }
    return `${DEFAULT_WRAPPER_HEAD}${bridge}${stripped}${DEFAULT_WRAPPER_TAIL}`;
  }, [detail]);

  const deleteMutation = useMutation({
    mutationFn: () => api.dashboardDelete(dashboardId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
      navigate({ to: '/chat' });
    },
  });

  const onShareCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/dashboard/view/${dashboardId}`,
      );
      setShareFeedback('copied');
      window.setTimeout(() => setShareFeedback('idle'), 2000);
    } catch {
      setShareFeedback('error');
      window.setTimeout(() => setShareFeedback('idle'), 2000);
    }
  };

  if (detailQuery.isLoading) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-background text-foreground-muted text-sm">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (detailQuery.isError || !detail) {
    const status = detailQuery.error instanceof ApiError ? detailQuery.error.status : 0;
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-background px-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">
            {status === 404 ? '대시보드를 찾을 수 없습니다' : '대시보드를 불러오지 못했습니다'}
          </h1>
          <p className="text-foreground-muted text-sm">
            {status === 404
              ? '존재하지 않거나, 접근 권한이 없습니다.'
              : '잠시 후 다시 시도해 주세요.'}
          </p>
          <Link
            to="/chat"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> 채팅으로
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col bg-surface-muted">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
        <Link
          to="/chat"
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          aria-label="채팅으로"
          title="채팅으로"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h2
          className="min-w-0 flex-1 truncate text-sm font-medium text-foreground"
          title={detail.title}
        >
          {detail.title}
          <span className="ml-2 text-xs text-foreground-muted">— {detail.ownerDisplayName}</span>
        </h2>

        <button
          type="button"
          aria-label="분석"
          onClick={() => setProfileOpen((v) => !v)}
          className={
            profileOpen
              ? 'grid size-7 place-items-center rounded-md bg-surface-muted text-foreground'
              : 'grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground'
          }
          title="분석 — 컬럼 프로파일 / 데이터 품질"
        >
          <Activity className="size-4" />
        </button>

        <button
          type="button"
          aria-label="공유 링크 복사"
          onClick={onShareCopyUrl}
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          title={shareFeedback === 'copied' ? '복사됨' : '공유 링크 복사 (URL)'}
        >
          {shareFeedback === 'copied' ? (
            <Check className="size-4" />
          ) : (
            <Share2 className="size-4" />
          )}
        </button>

        {detail.isOwner ? (
          <>
            <button
              type="button"
              aria-label="분석설계"
              onClick={() => setSettingsOpen(true)}
              className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
              title="분석설계 — direction / intent / 컬럼 라벨"
            >
              <Settings className="size-4" />
            </button>
            <button
              type="button"
              aria-label="공유 설정"
              onClick={() => setShareOpen(true)}
              className="h-7 rounded-md px-2 text-xs text-foreground-muted hover:bg-surface-muted hover:text-foreground"
              title="공유 범위 변경"
            >
              {detail.visibility === 'public'
                ? '전체'
                : detail.visibility === 'group'
                  ? '그룹'
                  : '비공개'}
            </button>
            <button
              type="button"
              aria-label="삭제"
              onClick={() => {
                if (window.confirm('이 대시보드를 삭제하시겠어요? 되돌릴 수 없습니다.')) {
                  deleteMutation.mutate();
                }
              }}
              className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-destructive/10 hover:text-destructive"
              title="삭제"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        ) : null}
      </header>

      <div className="relative flex-1 min-h-0 overflow-hidden bg-surface">
        <iframe
          ref={iframeRef}
          title={detail.title}
          sandbox="allow-scripts allow-modals"
          srcDoc={srcDoc}
          className="block h-full w-full border-0 bg-white"
        />
        {isExpired ? (
          // biome-ignore lint/a11y/useSemanticElements: ArtifactPanel.ExpiredOverlay 와 동일 패턴
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-0 flex items-center justify-center bg-surface/90 backdrop-blur-sm p-6 text-center"
          >
            <div className="max-w-md space-y-3">
              <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="size-6" />
              </div>
              <h3 className="text-base font-semibold">대시보드가 만료되었습니다</h3>
              <p className="text-sm text-foreground-muted">
                이 대시보드가 사용하는 저장된 쿼리가 제거되었습니다. 원본 대화에서 다시 만들어
                주세요.
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <DashboardSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        dashboard={detail}
      />
      <DashboardShareDialog open={shareOpen} onOpenChange={setShareOpen} dashboard={detail} />
      {profileOpen ? (
        <DashboardProfilePanel
          dashboardId={dashboardId}
          dashboard={detail}
          onClose={() => setProfileOpen(false)}
        />
      ) : null}
    </div>
  );
}
